/**
 * Admin — Settings layout route (6 sub-tabs).
 *
 * Sub-tabs:
 *   /admin/settings              — General (default; also the two tenant settings)
 *   /admin/settings/compliance   — Compliance profile, residency, retention
 *   /admin/settings/api          — API keys, webhooks, delivery log
 *   /admin/settings/integrations — SIEM, SMTP, Chat Ops, SCIM, Storage
 *   /admin/settings/data         — Backup, export, deletion, legal hold
 *
 * USR-F1-T4 (AC-07; decision 26 — every screen asks exactly the key its route
 * checks). Each tab carries the READ key its routes declare
 * (packages/api/src/routes/v1/admin/settings/*, config.permission) and is
 * drawn only when the server-reported grant set holds it (useHasPermission —
 * the browser computes nothing). A Member holds read:settings,
 * read:settings.compliance and read:settings.integrations; API & Webhooks
 * (read:settings.api) and Data & Backup (read:settings.data) are Admin keys.
 */

import { createFileRoute, Outlet, Link, useLocation } from '@tanstack/react-router';
import {
  Building2, ShieldCheck, Key, Plug, Database,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useHasPermission } from '@/lib/use-has-permission';

export const Route = createFileRoute('/admin/settings')({
  component: SettingsLayout,
});

type SettingsTabResource =
  | 'settings'
  | 'settings.compliance'
  | 'settings.api'
  | 'settings.integrations'
  | 'settings.data';

const SUB_TABS: ReadonlyArray<{
  label: string;
  path: string;
  icon: typeof Building2;
  action: 'read';
  resource: SettingsTabResource;
}> = [
  { label: 'General',        path: '/admin/settings',              icon: Building2,   action: 'read', resource: 'settings' },
  { label: 'Compliance',     path: '/admin/settings/compliance',   icon: ShieldCheck, action: 'read', resource: 'settings.compliance' },
  { label: 'API & Webhooks', path: '/admin/settings/api',          icon: Key,         action: 'read', resource: 'settings.api' },
  { label: 'Integrations',   path: '/admin/settings/integrations', icon: Plug,        action: 'read', resource: 'settings.integrations' },
  { label: 'Data & Backup',  path: '/admin/settings/data',         icon: Database,    action: 'read', resource: 'settings.data' },
];

function SettingsLayout() {
  const location = useLocation();
  // One literal ask per tab key (the server-reported grant set decides).
  const holds: Record<SettingsTabResource, boolean> = {
    settings: useHasPermission('read', 'settings'),
    'settings.compliance': useHasPermission('read', 'settings.compliance'),
    'settings.api': useHasPermission('read', 'settings.api'),
    'settings.integrations': useHasPermission('read', 'settings.integrations'),
    'settings.data': useHasPermission('read', 'settings.data'),
  };
  const tabs = SUB_TABS.filter((t) => holds[t.resource]);

  return (
    <div className="flex flex-col h-full">
      {/* Sub-tab bar */}
      <div className="border-b bg-background px-6 shrink-0">
        <nav className="flex items-center gap-1 overflow-x-auto whitespace-nowrap">
          {tabs.map(t => {
            const Icon = t.icon;
            const isActive = location.pathname === t.path
              || (t.path !== '/admin/settings' && location.pathname.startsWith(t.path));
            const isExact = location.pathname === '/admin/settings' || location.pathname === '/admin/settings/';
            const active = t.path === '/admin/settings' ? isExact : isActive;
            return (
              <Link
                key={t.path}
                to={t.path as any}
                title={t.label}
                aria-label={t.label}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors -mb-px shrink-0 whitespace-nowrap',
                  active
                    ? 'border-primary text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden md:inline">{t.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
